// Saving a long text of a card (R-U6, audit 31.10 P2-6): every save names the revision of the record the text was written on, so a
// stale text never overwrites a colleague's; a refusal keeps the text on screen and says why, with a way to send it again.

import type { CommandError, Id } from '@confdeck/core-shared';

/** What the saver needs of the sync client: a command with its answer, and the record as the server confirmed it. */
export interface SaveClient {
  run(command: string, input: unknown): Promise<{ ok: true; syncId: number } | { ok: false; error: CommandError }>;
  confirmed(id: Id): { rev: number; data: Record<string, unknown> } | undefined;
}

/** A save that did not go through: the text stays in the editor, the card says why. */
export interface SaveProblem { kind: 'conflict' | 'error'; message: string; md: string }

/** How many times a save is sent again when only other fields of the record changed meanwhile. */
const REBASE_TRIES = 3;

const text = (v: unknown) => (typeof v === 'string' ? v : '');

export class TextSaver {
  /** The server's text the editor's text is built on, and the record's revision then. */
  base: { text: string; rev: number };
  problem: SaveProblem | null = null;
  /** Called when `problem` changes. */
  onState: () => void = () => {};
  private queued: string | null = null;
  private busy = false;

  private readonly client: SaveClient;
  readonly id: Id;
  readonly field: string;

  constructor(client: SaveClient, id: Id, field: string) {
    this.client = client; this.id = id; this.field = field;
    const rec = client.confirmed(id);
    this.base = { text: text(rec?.data[field]), rev: rec?.rev ?? 0 };
  }

  get saving(): boolean { return this.busy || this.queued !== null; }

  /** The editor took the server's text (a colleague's edit came in while nobody wrote here): later saves are built on it. */
  take(): void {
    const rec = this.client.confirmed(this.id);
    if (rec) this.base = { text: text(rec.data[this.field]), rev: rec.rev };
  }

  /** Sends the text; a newer one typed meanwhile waits for the answer and goes next, on the revision that answer made. */
  async save(md: string): Promise<void> {
    this.queued = md;
    if (this.busy) return;
    this.busy = true;
    try {
      let tries = 0;
      while (this.queued !== null) {
        const next: string = this.queued;
        this.queued = null;
        const value = next.trim() ? next : null;
        if ((value ?? '') === this.base.text && !this.problem) continue;
        const r = await this.client.run('record.update', { id: this.id, data: { [this.field]: value }, rev: this.base.rev });
        const rec = this.client.confirmed(this.id);
        if (r.ok) {
          this.base = { text: value ?? '', rev: rec?.rev ?? this.base.rev };
          this.setProblem(null);
          tries = 0;
          continue;
        }
        if (r.error.code === 'conflict' && rec && text(rec.data[this.field]) === this.base.text && tries < REBASE_TRIES) {
          // Another field changed (a stage, a date): the text is still the one this was written on — send it again on the new revision.
          tries++;
          this.base.rev = rec.rev;
          this.queued ??= next;
          continue;
        }
        const conflict = r.error.code === 'conflict' && !!rec && text(rec.data[this.field]) !== this.base.text;
        this.setProblem(conflict
          ? { kind: 'conflict', md: this.queued ?? next, message: 'Текст изменил другой человек, пока вы писали. Ваш вариант не сохранён.' }
          : { kind: 'error', md: this.queued ?? next, message: `Текст не сохранён: ${r.error.message}` });
        this.queued = null;
      }
    } finally { this.busy = false; }
  }

  /** «Повторить» or «Сохранить мой вариант»: the text goes again, on the server's current revision when the person chose theirs. */
  retry(): Promise<void> {
    const p = this.problem;
    if (!p) return Promise.resolve();
    if (p.kind === 'conflict') this.take();
    this.setProblem(null);
    return this.save(p.md);
  }

  /** «Взять их текст»: the person drops their version; returns the server's text for the editor. */
  drop(): string {
    this.take();
    this.setProblem(null);
    return this.base.text;
  }

  private setProblem(p: SaveProblem | null): void {
    if (p === this.problem) return;
    this.problem = p;
    this.onState();
  }
}
